'use client';

import Image from 'next/image';
import { type JSX, useState } from 'react';

import { telemetryClickHandler } from '../telemetryHandler';
import type { MobileNavItemConfig } from '../types';

const AI_GRADIENT_BG = 'linear-gradient(to right, #9952E0 0%, #FF66E6 100%)';

interface MobileAccordionItemProps {
    config: MobileNavItemConfig;
}

export default function MobileAccordionItem({ config }: MobileAccordionItemProps): JSX.Element {
    // RFPC-1277: default to expanded so users see all nested links
    // (incl. "Talk to Sales" on Businesses) without an extra tap.
    const [isExpanded, setIsExpanded] = useState(true);
    const { dropdown, hasGradientText, gradientIcon } = config;

    const handleClick = (): void => {
        telemetryClickHandler(config.telemetrySlug, config.telemetryEvents)?.();
        if (dropdown) {
            setIsExpanded((prev) => !prev);
        } else if (config.href) {
            window.location.href = config.href;
        }
    };

    return (
        <div>
            {/* Divider */}
            {config.showDividerAbove && <div className="mb-6 h-px bg-gray-200" />}

            {/* Header row */}
            <button
                type="button"
                onClick={handleClick}
                aria-expanded={dropdown ? isExpanded : undefined}
                className="flex w-full items-center justify-between"
            >
                {/* Label */}
                {hasGradientText ? (
                    <span className="flex items-center gap-1">
                        {gradientIcon && <Image src={gradientIcon} alt="" width={9} height={9} />}
                        <span
                            className="text-sm font-semibold"
                            style={{
                                background: AI_GRADIENT_BG,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                color: 'transparent',
                            }}
                        >
                            {config.label}
                        </span>
                    </span>
                ) : (
                    <span className="text-sm font-semibold text-gray-900">{config.label}</span>
                )}

                {/* Chevron (only if has dropdown) */}
                {dropdown && (
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        className={`shrink-0 transition-transform duration-200 ${isExpanded ? '-rotate-90' : 'rotate-90'}`}
                    >
                        <path
                            d="M6 4L10 8L6 12"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                )}
            </button>

            {/* Expanded content */}
            {isExpanded && dropdown && (
                <div className="flex flex-col gap-2 pt-2">
                    {/* Links */}
                    <div className="flex flex-col gap-6 px-2 pb-2 pt-4">
                        {dropdown.links.map((link) => {
                            if (!link.subItems || link.subItems.length === 0) {
                                return (
                                    <a
                                        key={link.href}
                                        href={link.href}
                                        className="text-sm text-gray-800"
                                        onClick={telemetryClickHandler(link.telemetrySlug, link.telemetryEvents)}
                                    >
                                        {link.label}
                                    </a>
                                );
                            }

                            return (
                                <div key={link.href} className="flex flex-col gap-4">
                                    <a
                                        href={link.href}
                                        className="text-sm text-gray-900"
                                        onClick={telemetryClickHandler(link.telemetrySlug, link.telemetryEvents)}
                                    >
                                        {link.label}
                                    </a>
                                    <div className="flex flex-col gap-4 pl-4">
                                        {link.subItems.map((subItem) => (
                                            <a
                                                key={subItem.href}
                                                href={subItem.href}
                                                className="text-xs text-gray-900"
                                                onClick={telemetryClickHandler(
                                                    subItem.telemetrySlug,
                                                    subItem.telemetryEvents,
                                                )}
                                            >
                                                {subItem.label}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Categories */}
                    {dropdown.categories && (
                        <div className="flex flex-col gap-4 rounded-md bg-gray-100 p-3">
                            <span className="text-xs font-semibold text-gray-700">{dropdown.categories.title}</span>
                            {dropdown.categories.items.map((item) => (
                                <a
                                    key={item.href}
                                    href={item.href}
                                    className="text-xs text-gray-800"
                                    onClick={telemetryClickHandler(item.telemetrySlug, item.telemetryEvents)}
                                >
                                    {item.label}
                                </a>
                            ))}
                        </div>
                    )}

                    {/* CTA */}
                    {dropdown.cta && (
                        <a
                            href={dropdown.cta.href}
                            className="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-blue-700 text-sm font-semibold text-white hover:bg-blue-800"
                            onClick={telemetryClickHandler(dropdown.cta.telemetrySlug, dropdown.cta.telemetryEvents)}
                        >
                            {dropdown.cta.label}
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path
                                    d="M3 8H13M13 8L9 4M13 8L9 12"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </a>
                    )}
                </div>
            )}
        </div>
    );
}
